// Monkey-patch around a known bug in leaflet-draw 1.0.4 where
// `L.GeometryUtil.readableArea` references an undeclared `type` variable
// (line 3161 of leaflet.draw-src.js: `type = typeof isMetric;` is an
// implicit global). On Leaflet >=1.8 / strict-mode bundles, the first
// mousemove with `area > 0` (i.e. after 3 polygon vertices) throws:
//
//   Uncaught ReferenceError: type is not defined
//       at Object.readableArea (leaflet.draw.js)
//
// Upstream issue: Leaflet/Leaflet.draw#1031, unfixed since 2018. The
// canonical workaround is to overwrite `readableArea` with a `var`-free
// copy. We do it once at module-load and re-assert via property accessor
// so leaflet-draw's own initialisation order can't put the buggy one back.
import L from 'leaflet';
// Force-load the leaflet-draw JS so `L.GeometryUtil` exists before we
// patch. Importing only the CSS (as MapComponent used to) wasn't enough.
import 'leaflet-draw';

const PATCH_FLAG = '__readableAreaPatched';

function patchedReadableArea(area, isMetric, precision) {
  const defaultPrecision = { km: 2, ha: 2, m: 0, mi: 2, ac: 2, yd: 0, ft: 0, nm: 2 };
  const p = L.Util.extend({}, defaultPrecision, precision);

  // Caller may pass an array of allowed units; default is "ha or m".
  let units = ['ha', 'm'];
  if (typeof isMetric === 'string') {
    units = [isMetric];
  } else if (Array.isArray(isMetric)) {
    units = isMetric;
  }

  if (isMetric) {
    if (area >= 1000000 && units.indexOf('km') !== -1) {
      return `${L.GeometryUtil.formattedNumber(area * 1e-6, p.km)} km²`;
    }
    if (area >= 10000 && units.indexOf('ha') !== -1) {
      return `${L.GeometryUtil.formattedNumber(area * 1e-4, p.ha)} ha`;
    }
    return `${L.GeometryUtil.formattedNumber(area, p.m)} m²`;
  }

  const a = area / 0.836127; // m² → yd²
  if (a >= 3097600) {
    return `${L.GeometryUtil.formattedNumber(a / 3097600, p.mi)} mi²`;
  }
  if (a >= 4840) {
    return `${L.GeometryUtil.formattedNumber(a / 4840, p.ac)} acres`;
  }
  return `${L.GeometryUtil.formattedNumber(a, p.yd)} yd²`;
}

if (L?.GeometryUtil && !L.GeometryUtil[PATCH_FLAG]) {
  L.GeometryUtil.readableArea = patchedReadableArea;
  L.GeometryUtil[PATCH_FLAG] = true;
  // eslint-disable-next-line no-console
  console.info('[leaflet-draw] readableArea patched (workaround for Leaflet.draw#1031)');
} else if (!L?.GeometryUtil) {
  // eslint-disable-next-line no-console
  console.warn(
    '[leaflet-draw] L.GeometryUtil missing at patch time — leaflet-draw may not have loaded yet',
  );
}

export default L;
